import { Upload } from "lucide-react";

import { Field } from "@/components/form-field";
import { Input } from "@/components/ui/input";
import type { ClientMazeInput } from "@/legacy/client-maze";

export function ClientMazeStep({
  value,
  onChange,
}: {
  value: ClientMazeInput;
  onChange: (next: ClientMazeInput) => void;
}) {
  const set = <K extends keyof ClientMazeInput>(key: K, v: ClientMazeInput[K]) =>
    onChange({ ...value, [key]: v });
  return (
    <>
      <div className="bg-muted/50 rounded-md border p-3 text-xs">
        <p className="font-medium">Upload the exact file students download.</p>
        <p className="text-muted-foreground mt-1">
          Its md5 is recorded now and compared against every submission. A file that
          differs by one byte from the one on Drive rejects every run as{" "}
          <span className="font-mono">Maze mismatch</span> — 28 of the 45 failures in
          the last term. It is decrypted with the server&apos;s{" "}
          <code className="font-mono">COURSE_TAG</code>, so it must come from the build
          this server is configured for.
        </p>
      </div>

      <Field id="c-maze" label="Encrypted maze file" required>
        <label
          htmlFor="c-maze"
          className="hover:bg-muted/50 flex cursor-pointer items-center gap-3 rounded-md border border-dashed p-4"
        >
          <Upload className="text-muted-foreground size-5" aria-hidden />
          <span className="text-sm">{value.file ? value.file.name : "Choose maze.json"}</span>
        </label>
        <input
          id="c-maze"
          type="file"
          accept=".json"
          className="hidden"
          onChange={(e) => set("file", e.target.files?.[0] ?? null)}
        />
      </Field>

      <div className="grid gap-4 sm:grid-cols-2">
        <Field
          id="c-maze-id"
          label="Maze Drive file id"
          required
          hint={
            <>
              The id only. Goes into each student&apos;s{" "}
              <code className="font-mono">startup.json</code>.
            </>
          }
        >
          <Input
            id="c-maze-id"
            value={value.maze_id}
            onChange={(e) => set("maze_id", e.target.value)}
            className="font-mono text-xs"
            placeholder="1a2B3c..."
          />
        </Field>
        <Field
          id="c-data"
          label="Exploration dataset URL"
          required
          hint="Where students download the training images."
        >
          <Input
            id="c-data"
            value={value.exploration_data_url}
            onChange={(e) => set("exploration_data_url", e.target.value)}
            placeholder="https://drive.google.com/..."
          />
        </Field>
      </div>
    </>
  );
}
